import { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { debounce, get, isEmpty } from 'lodash';

import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Card, showToast } from '@repo/ui';
import { GroupByOption, AppealStatus, SortOrder, DeadlineTier } from '@repo/types';
import type { SelectOption } from '@repo/ui';
import { useQueryClient } from '@tanstack/react-query';
import { MultiValue } from 'react-select';

import { useTableParams } from '../../hooks';
import {
  useAppeals,
  useGetAppealsByGroupId,
  useGetAppealsSelected,
  useRollbackInvoice,
  useBulkAssignAppealsClerk,
  useClerkUserMap,
} from '../../queries';
import { Appeal, AppealGroup } from '../../types';
import { DataTableColumn } from '@xtnd/web/shared/components/DataTable';
import AppealsHeader from './AppealHeader';
import AppealsTable from './AppealsTable';

import { generateAppealsColumns } from './AppealsColumns';
import { QueryStringParams } from '@xtnd/web/shared';
import { useDarkMode } from '@xtnd/web/shared/hooks/useDarkMode';
import InvoiceDetailDrawer from './InvoiceDetailDrawer';
import { useInvoiceContext } from '../common/InvoiceContext';
import { AppealActivity } from './AppealActivity';
import { APPEAL_STATUS_OPTIONS } from '../../constants';
import { invoiceApi } from '../../api';
import { RollbackInvoiceModal } from '../common/RollbackInvoiceModal';
import { useArAppealSelectionsStore } from '@xtnd/web/shared/stores';
import { EditAppealModal } from './EditAppealModal';
import { MarkWriteOffRemainingModal } from './MarkWriteOffRemainingModal';
import { useCreateCutsAppliedWriteOff } from '../../queries';
import { getApiErrorMessage } from '../../utils/apiErrorHandler';

// Define the set of status values that are NOT CLOSED.
const NON_CLOSED_STATUSES = [
  AppealStatus.PRE_APPEAL,
  AppealStatus.APPEAL_SUBMITTED,
  AppealStatus.NO_APPEAL,
  AppealStatus.DEADLINE_EXPIRED,
  AppealStatus.APPEAL_APPROVED,
  AppealStatus.APPEAL_DENIED,
  AppealStatus.TO_BE_APPEALED,
  AppealStatus.AWAITING_APPEAL_SUBMISSION,
];

/**
 * @description Main component for managing Invoice Appeals.
 * It handles the global state for the DataTable (pagination, filters)
 * and controls the view based on 'groupBy' and 'rowSelected' states.
 */
export default function Appeals() {
  const queryClient = useQueryClient();
  const { openInvoiceDetailDrawer } = useInvoiceContext();
  const { isDark } = useDarkMode();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const locationKeyRef = useRef(location.key);
  const navigate = useNavigate();

  // 1. GLOBAL STATE MANAGEMENT
  const { tableParams, setParams } = useTableParams({
    page: 1,
    limit: 25,
    filters: {
      groupBy: GroupByOption.NONE,
      status: [AppealStatus.TO_BE_APPEALED],
    },
    sorts: {
      maxDaysPastDue: SortOrder.DESC,
    },
  });

  const groupBy: GroupByOption =
    (get(tableParams, 'filters.groupBy') as GroupByOption | undefined) ?? GroupByOption.NONE;

  // Determine initial 'Include Closed Invoices' state
  const isInitialClosedChecked = get(tableParams, 'filters.status', []).includes(
    AppealStatus.CLOSED
  );
  const [isCheckedClosedInvoices, setIsCheckedClosedInvoices] = useState(isInitialClosedChecked);

  // Helper to compute initial status selection from URL params
  const getInitialStatusSelected = (): SelectOption[] => {
    // If URL has no explicit filters param, it's initial load - keep empty
    const filtersParam = searchParams.get('filters');
    if (!filtersParam) {
      return [];
    }

    // Parse status from URL filter
    const urlStatus = get(tableParams, 'filters.status', []) as AppealStatus[];
    if (isEmpty(urlStatus)) {
      return [];
    }

    // Check if status was explicitly set by user
    const isStatusExplicit = get(tableParams, 'filters.statusExplicit', false);

    // If status was explicitly set by user, always show it
    if (isStatusExplicit) {
      const mode = isDark ? 'dark' : 'light';
      return urlStatus
        .map(
          status => APPEAL_STATUS_OPTIONS[mode][status as keyof typeof APPEAL_STATUS_OPTIONS.light]
        )
        .filter(Boolean);
    }

    // If URL status equals default NON_CLOSED_STATUSES and not explicit, keep dropdown empty
    // This handles the case when groupBy switch sets default statuses to URL
    const isDefaultStatuses =
      urlStatus.length === NON_CLOSED_STATUSES.length &&
      urlStatus.every(s => NON_CLOSED_STATUSES.includes(s));
    if (isDefaultStatuses) {
      return [];
    }

    // Map status values to SelectOptions
    const mode = isDark ? 'dark' : 'light';
    return urlStatus
      .map(
        status => APPEAL_STATUS_OPTIONS[mode][status as keyof typeof APPEAL_STATUS_OPTIONS.light]
      )
      .filter(Boolean);
  };

  // State for search/filter inputs
  const [search, setSearch] = useState(get(tableParams, 'filters.search') ?? '');
  const [statusSelected, setStatusSelected] = useState<SelectOption[]>(getInitialStatusSelected);
  const [clientDeadlineTier, setClientDeadlineTier] = useState<DeadlineTier | ''>('');
  const [assignedTo, setAssignedTo] = useState<SelectOption[] | null>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);

  const [actionSelectedAppeal, setActionSelectedAppeal] = useState<Appeal | null>(null);
  const [actionRef, setActionRef] = useState<HTMLButtonElement | null>(null);
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  // Track whether CLOSED was selected via dropdown (show badge) or checkbox (hide badge)
  const closedSelectedViaDropdownRef = useRef(false);
  const [isActivityOpen, setIsActivityOpen] = useState(false);
  const [selectedActivityAppeal, setSelectedActivityAppeal] = useState<Appeal | null>(null);

  // State for row mutation loading (e.g., status update in a row)
  const [mutatingRows, setMutatingRows] = useState<Set<string>>(new Set());
  // Note: toggleSelection and selectAll are now used directly in AppealCheckbox.tsx
  const { selectedKeys, clearSelections } = useArAppealSelectionsStore();

  // Rollback confirmation dialog state
  const [isRollbackDialogOpen, setIsRollbackDialogOpen] = useState(false);
  const [appealToRollback, setAppealToRollback] = useState<Appeal | null>(null);
  const [isRollbackLoading, setIsRollbackLoading] = useState(false);

  const {
    clerkUsersOptions: assigneeOptions,
    clerkUserMap,
    isLoading: isLoadingClerks,
  } = useClerkUserMap();

  const [isBulkAssigning, setIsBulkAssigning] = useState(false);

  const { data: rowSelectedData } = useGetAppealsSelected({
    enabled: !!selectedInvoiceId,
  });

  const rowSelected = useMemo(() => rowSelectedData ?? ({} as AppealGroup), [rowSelectedData]);

  const resetGroupSelection = useCallback(() => {
    setSelectedInvoiceId(null);
    setAppealSelectedStatus([]);
    setAppealSelectedSearch('');
    setAssignedToLocal(null);
    setIsCheckedClosedInvoicesSelected(false);
    queryClient.setQueryData(['appealsSelected'], {} as AppealGroup);
    setAppealSelectedParams({
      filters: { status: NON_CLOSED_STATUSES, search: '' },
      page: 1,
      limit: 25,
    });
  }, [queryClient]);

  // Clear detail selection when navigation (e.g., sidebar click) happens
  useEffect(() => {
    if (location.key !== locationKeyRef.current) {
      locationKeyRef.current = location.key;
      if (!isEmpty(rowSelected)) {
        resetGroupSelection();
      }
    }
  }, [location.key, rowSelected, resetGroupSelection]);

  useEffect(() => {
    const queryKey = ['appealsSelected'];
    if (!selectedInvoiceId) {
      queryClient.setQueryData(queryKey, {} as AppealGroup);
    }
  }, [selectedInvoiceId, queryClient]);

  // 2. DATA FETCHING
  const { data, isFetching, error, refetch } = useAppeals({ ...tableParams }, { enabled: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const appealsData = useMemo(() => data?.data ?? [], [data, statusSelected]);
  const paginationData = useMemo(
    () => ({
      total: data?.pagination.total ?? 0,
      page: tableParams.page,
      limit: tableParams.limit,
    }),
    [data, tableParams.page, tableParams.limit]
  );

  // Bulk assign mutation
  const { mutateAsync: bulkAssign } = useBulkAssignAppealsClerk();

  // Handler that auto-triggers bulk assign/unassign when a clerk is selected or unassign clicked
  const handleAssigneeChange = useCallback(
    async (clerkEmail: string) => {
      if (selectedKeys.length === 0) return;

      const isUnassign = !clerkEmail || clerkEmail.trim() === '';

      setIsBulkAssigning(true);
      try {
        const response = await bulkAssign({
          invoiceIds: selectedKeys,
          assignedTo: isUnassign ? null : clerkEmail,
        });

        const { successCount, errorCount } = response.data;

        if (errorCount > 0) {
          const action = isUnassign ? 'unassigned' : 'assigned';
          showToast.warning(`${action} ${successCount} invoice(s), but ${errorCount} failed.`);
        } else {
          if (isUnassign) {
            showToast.success(`Successfully unassigned ${successCount} invoice(s)`);
          } else {
            const selectedOption = assigneeOptions.find(opt => opt.value === clerkEmail);
            const displayName = selectedOption?.label || clerkEmail;
            showToast.success(`Successfully assigned ${successCount} invoice(s) to ${displayName}`);
          }
        }

        clearSelections();
        // Cache is updated by mutation's onSuccess, no need to refetch
      } finally {
        setIsBulkAssigning(false);
      }
    },
    [selectedKeys, clearSelections, assigneeOptions, bulkAssign]
  );

  useEffect(() => {
    const filterStatuses: AppealStatus[] = get(tableParams, 'filters.status', []);
    if (!Array.isArray(filterStatuses) || filterStatuses.length === 0) {
      setStatusSelected([]);
      return;
    }

    // Check if status was explicitly set by user (not system default)
    const isStatusExplicit = get(tableParams, 'filters.statusExplicit', false);

    // Filter out CLOSED status ONLY if it was added via checkbox, not dropdown
    const statusesForDropdown = filterStatuses.filter(
      status => status !== AppealStatus.CLOSED || closedSelectedViaDropdownRef.current
    );

    // If NOT explicitly set by user and matches default NON_CLOSED_STATUSES, clear dropdown
    // This handles the "X" clear button case - show empty but filter by defaults
    if (!isStatusExplicit) {
      const hasAllNonClosed =
        statusesForDropdown.length === NON_CLOSED_STATUSES.length &&
        statusesForDropdown.every(status =>
          (NON_CLOSED_STATUSES as AppealStatus[]).includes(status)
        );
      if (hasAllNonClosed) {
        setStatusSelected([]);
        return;
      }
    }

    const mode = isDark ? 'dark' : 'light';
    const optionsByStatus = APPEAL_STATUS_OPTIONS[mode];
    const mappedOptions = statusesForDropdown
      .map(statusValue => optionsByStatus[statusValue as keyof typeof optionsByStatus])
      .filter(Boolean) as SelectOption[];

    setStatusSelected(mappedOptions);
  }, [tableParams, isDark]);

  useEffect(() => {
    const assignedIds = get(tableParams, 'filters.assignedTo');

    if (isEmpty(assignedIds) || !Array.isArray(assignedIds)) {
      setAssignedTo([]);
      return;
    }

    if (isEmpty(assigneeOptions)) {
      return;
    }

    // Include "Unassigned" option when mapping back from URL params
    const allOptions = [{ label: 'Unassigned', value: 'unassigned' }, ...assigneeOptions];
    const mappedOptions = allOptions.filter(opt => assignedIds.includes(String(opt.value)));

    setAssignedTo(mappedOptions);
  }, [tableParams, assigneeOptions]);

  const [appealSelectedParams, setAppealSelectedParams] = useState<QueryStringParams>({
    page: 1,
    limit: 25,
    filters: {
      status: NON_CLOSED_STATUSES,
      search: '',
    },
  });

  const [appealSelectedSearch, setAppealSelectedSearch] = useState(
    get(appealSelectedParams, 'filters.search') ?? ''
  );
  const [appealSelectedStatus, setAppealSelectedStatus] = useState<SelectOption[]>([]);
  const [assignedToLocal, setAssignedToLocal] = useState<SelectOption[] | null>(
    get(appealSelectedParams, 'filters.assignedTo') || []
  );
  const [isCheckedClosedInvoicesSelected, setIsCheckedClosedInvoicesSelected] = useState(false);

  const appealsByGroupIdParams = useMemo(
    () => ({
      ...appealSelectedParams,
      filters: {
        ...appealSelectedParams.filters,
        groupBy,
      },
    }),
    [appealSelectedParams, groupBy]
  );

  const {
    data: appealsByGroupIdData,
    isFetching: isFetchingAppealsByGroupId,
    refetch: refetchAppealsByGroupId,
  } = useGetAppealsByGroupId(rowSelected.groupId || '', appealsByGroupIdParams, {
    enabled:
      !isEmpty(rowSelected) &&
      (groupBy === GroupByOption.CLIENT || groupBy === GroupByOption.RESPONSIBLE),
  });

  // 3. GLOBAL HANDLERS

  /**
   * @description Toggles the inclusion of CLOSED invoices in the global filter.
   */
  const handleCheckedClosedInvoices = useCallback(() => {
    const nextChecked = !isCheckedClosedInvoices;
    setIsCheckedClosedInvoices(nextChecked);
    clearSelections();

    // Only reset ref when unchecking - preserve badge if it was selected via dropdown
    if (!nextChecked) {
      closedSelectedViaDropdownRef.current = false;
      // Also remove CLOSED from dropdown display when unchecking
      setStatusSelected(prev => prev.filter(opt => opt.value !== AppealStatus.CLOSED));
    }

    // Get current filter status, default to non-closed statuses if empty/undefined
    const currentFilterStatus = get(tableParams, 'filters.status', NON_CLOSED_STATUSES);

    let nextStatus: AppealStatus[];
    if (nextChecked) {
      // Add CLOSED status if not already present, ensuring uniqueness
      nextStatus = [...new Set([...currentFilterStatus, AppealStatus.CLOSED])];
    } else {
      // Filter out the CLOSED status
      nextStatus = currentFilterStatus.filter(
        (status: AppealStatus) => status !== AppealStatus.CLOSED
      );
      // Ensure we don't send an empty array if all non-closed were removed somehow
      if (isEmpty(nextStatus)) {
        nextStatus = NON_CLOSED_STATUSES;
      }
    }

    setParams({
      ...tableParams,
      page: 1,
      filters: {
        ...tableParams.filters,
        status: nextStatus,
      },
    });
  }, [isCheckedClosedInvoices, tableParams, setParams, clearSelections]);

  /**
   * @description Handles the change of the grouping option (None, Client, or Responsible Attorney).
   * @param {GroupByOption} value - The selected group by option.
   */
  const handleSelectedGroupBy = useCallback(
    (value: GroupByOption) => {
      setSelectedInvoiceId(null);
      const queryKey = ['appealsSelected'];
      queryClient.setQueryData(queryKey, {} as AppealGroup);
      setIsCheckedClosedInvoices(false);
      setClientDeadlineTier('');
      setStatusSelected([]); // Reset local dropdown state
      clearSelections();
      setParams({
        ...tableParams,
        page: 1,
        filters: {
          ...tableParams.filters,
          status: NON_CLOSED_STATUSES,
          statusExplicit: false,
          deadlineTier: undefined,
          groupBy: value,
          assignedTo: undefined,
        },
      });
    },
    [queryClient, setParams, tableParams, clearSelections]
  );

  /**
   * @description Handles the selection/deselection of appeal statuses in the global filter.
   * @param {MultiValue<SelectOption> | null} value - Selected status options.
   */
  const handleSelectedStatus = useCallback(
    (value: SelectOption[] | null) => {
      const selectedOptions = value ?? [];
      setStatusSelected(selectedOptions);
      clearSelections();
      const statusValues = selectedOptions.map(item => item.value as AppealStatus);

      // Track whether CLOSED was selected via dropdown
      const includesClosed = statusValues.includes(AppealStatus.CLOSED);
      closedSelectedViaDropdownRef.current = includesClosed;

      // If CLOSED was removed from dropdown, also uncheck the checkbox
      if (!includesClosed && isCheckedClosedInvoices) {
        setIsCheckedClosedInvoices(false);
      }

      if (isEmpty(selectedOptions)) {
        const status = [...NON_CLOSED_STATUSES];
        if (isCheckedClosedInvoices) {
          status.push(AppealStatus.CLOSED);
        }
        // When cleared via X, reset explicit flag so dropdown stays empty on reload
        return setParams({
          ...tableParams,
          page: 1,
          filters: {
            ...tableParams.filters,
            status,
            statusExplicit: false,
          },
        });
      }

      setParams({
        ...tableParams,
        page: 1,
        filters: {
          ...tableParams.filters,
          // If statusValues is empty, set filter to undefined to remove it from params
          status: isEmpty(statusValues) ? undefined : statusValues,
          statusExplicit: true,
        },
      });
    },
    [tableParams, setParams, isCheckedClosedInvoices, clearSelections]
  );

  /**
   * @description Handles the change of the deadline tier filter.
   * @param {DeadlineTier | ''} value - The selected deadline tier value.
   */
  const handleClientDeadlineTierChange = useCallback(
    (value: DeadlineTier | '') => {
      setClientDeadlineTier(value);
      clearSelections();
      // Update global params
      setParams({
        ...tableParams,
        page: 1,
        filters: {
          ...tableParams.filters,
          deadlineTier: value || undefined,
        },
      });
      // Update client detail params
      setAppealSelectedParams(prev => ({
        ...prev,
        page: 1,
        filters: {
          ...prev.filters,
          deadlineTier: value || undefined,
        },
      }));
    },
    [tableParams, setParams, clearSelections]
  );

  /**
   * @description Handles the change of the assigned to filter.
   * @param {string | null} value - The selected Appeals Clerk email or null to clear.
   */
  const handleFiltersByAssignedTo = useCallback(
    (value: SelectOption[] | null) => {
      setAssignedTo(value || []);
      // Update global params
      setParams({
        ...tableParams,
        page: 1,
        filters: {
          ...tableParams.filters,
          assignedTo: value?.map(v => v.value) || undefined,
        },
      });
    },
    [tableParams, setParams]
  );

  /**
   * @description Handles the change of the assigned to filter in local view.
   * @param {string | null} value - The selected Appeals Clerk email or null to clear.
   */
  const handleFilterByAssignedToLocal = useCallback((value: SelectOption[] | null) => {
    setAssignedToLocal(value);
    setAppealSelectedParams(prev => ({
      ...prev,
      page: 1,
      filters: {
        ...prev.filters,
        assignedTo: value?.map(v => v.value) || undefined,
      },
    }));
  }, []);

  /**
   * @description Performs the actual search update in tableParams after debounce.
   * @param {string} value - The search string.
   */
  const performSearch = useCallback(
    (value: string) => {
      clearSelections();
      setParams({
        ...tableParams,
        page: 1,
        filters: {
          ...tableParams.filters,
          search: value,
        },
      });
      setSelectedInvoiceId(null);
      const queryKey = ['appealsSelected'];
      queryClient.setQueryData(queryKey, {} as AppealGroup);
    },
    [setParams, tableParams, queryClient, clearSelections]
  );

  const debouncedSearch = useMemo(() => debounce(performSearch, 500), [performSearch]);

  /**
   * @description Handler for search input that sets local state and triggers debounce.
   * @param {string} value - The value from the search input.
   */
  const handleSearch = (value: string) => {
    setSearch(value);
    debouncedSearch(value);
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  /**
   * @description Handles the row click event. When grouping is by client, this action
   * sets the selected client group, resets local pagination/filters, and triggers
   * a local loading state. It also smoothly scrolls the viewport to the top.
   *
   * @param {Appeal | AppealGroup} row - The data of the row that was clicked.
   * It can be an individual Appeal object or an AppealGroup object
   * depending on the current view state.
   * @returns {Promise<void>} A Promise that resolves after the loading state
   * is complete (after the 300ms delay).
   */
  const handleRowClicked = async (row: Appeal | AppealGroup) => {
    if (groupBy === GroupByOption.CLIENT || groupBy === GroupByOption.RESPONSIBLE) {
      clearSelections();
      const queryKey = ['appealsSelected'];
      queryClient.setQueryData(queryKey, row as AppealGroup);

      // Sync local filter with global "Incl. Closed" state
      const localStatus = isCheckedClosedInvoices
        ? [...NON_CLOSED_STATUSES, AppealStatus.CLOSED]
        : NON_CLOSED_STATUSES;

      setAppealSelectedParams({
        ...appealSelectedParams,
        page: 1,
        filters: {
          ...appealSelectedParams.filters,
          status: localStatus,
        },
      });
      setIsCheckedClosedInvoicesSelected(isCheckedClosedInvoices);

      await new Promise(resolve => setTimeout(resolve, 300));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    handleOpenAppealDetail(row as Appeal);
  };

  const handleOpenAppealDetail = (appeal: Appeal) => {
    const { invoiceId } = appeal as Appeal;

    if (invoiceId) {
      openInvoiceDetailDrawer(invoiceId);
    }
  };

  /**
   * @description Manages the loading state for individual row mutations (e.g., status changes).
   * @param {string} invoiceId - The ID of the invoice being mutated.
   * @param {boolean} isPending - True if mutation is in progress, false otherwise.
   */
  const handleMutationStateChange = useCallback((invoiceId: string, isPending: boolean) => {
    setMutatingRows(prev => {
      const next = new Set(prev);
      if (isPending) {
        next.add(invoiceId);
      } else {
        next.delete(invoiceId);
      }
      return next;
    });
  }, []);

  /**
   * @description Custom function to apply CSS class to a row based on mutation status.
   * @param {Appeal | AppealGroup} row - The row data.
   * @returns {string} The CSS class string.
   */
  const getRowClassName = useCallback(
    (row: Appeal | AppealGroup) => {
      const baseClass = 'hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150';
      // If the row is currently mutating, apply a loading/highlight class
      if (mutatingRows.has(get(row, 'invoiceId', ''))) {
        return `${baseClass} bg-gray-50 dark:bg-gray-700 opacity-60 pointer-events-none cursor-not-allowed`;
      }
      return baseClass;
    },
    [mutatingRows]
  );

  const handleCopyAttorneyResponseLink = useCallback(
    async (appeal: Appeal) => {
      try {
        const invoiceId = appeal.invoiceId;

        const invoice = await queryClient.fetchQuery({
          queryKey: ['invoices', invoiceId],
          queryFn: () => invoiceApi.getInvoice(invoiceId),
          staleTime: 0,
          gcTime: 0,
          retry: 1,
        });

        const appealId = get(invoice, 'appeal.appealId');

        if (!appealId) {
          showToast.error('No appeal is associated with this invoice.');
          return;
        }

        const url = `${import.meta.env.VITE_APP_URL}/ar-collections/public/invoice/${invoiceId}/appeal/${appealId}/attorney-response`;
        await navigator.clipboard.writeText(url);
        showToast.success('The attorney response link has been copied to your clipboard.');
      } catch (_error) {
        showToast.error('Failed to generate the attorney response link.');
      }
    },
    [queryClient]
  );

  const handleViewAttorneyResponse = useCallback(
    async (appeal: Appeal) => {
      try {
        const invoiceId = appeal.invoiceId;

        const invoice = await queryClient.fetchQuery({
          queryKey: ['invoices', invoiceId],
          queryFn: () => invoiceApi.getInvoice(invoiceId),
          staleTime: 0,
          gcTime: 0,
          retry: 1,
        });

        const appealId = get(invoice, 'appeal.appealId');

        if (!appealId) {
          showToast.error('No appeal is associated with this invoice.');
          return;
        }

        navigate(
          `/ar-collections/public/invoice/${invoiceId}/appeal/${appealId}/attorney-response`
        );
      } catch (_error) {
        showToast.error('Failed to view the attorney response.');
      }
    },
    [navigate]
  );

  const { mutateAsync: rollbackInvoice } = useRollbackInvoice();

  const handleRollbackClick = useCallback((appeal: Appeal) => {
    setAppealToRollback(appeal);
    setIsRollbackDialogOpen(true);
  }, []);

  const handleRollbackConfirm = useCallback(async (targetStatus: string, comment: string) => {
    if (!appealToRollback) return;

    try {
      setIsRollbackLoading(true);
      handleMutationStateChange(appealToRollback.invoiceId, true);
      await rollbackInvoice({
        invoiceId: appealToRollback.invoiceId,
        rollbackTarget: targetStatus,
        comment,
      });
    } finally {
      setIsRollbackLoading(false);
      handleMutationStateChange(appealToRollback.invoiceId, false);
      setIsRollbackDialogOpen(false);
      setAppealToRollback(null);
    }
  }, [appealToRollback, rollbackInvoice, handleMutationStateChange]);

  const handleRollbackCancel = useCallback(() => {
    if (isRollbackLoading) return;
    setIsRollbackDialogOpen(false);
    setAppealToRollback(null);
  }, [isRollbackLoading]);

  // Edit modal state
  const [editingAppeal, setEditingAppeal] = useState<Appeal | null>(null);

  const handleEditClick = useCallback((appeal: Appeal) => {
    setEditingAppeal(appeal);
  }, []);

  const handleEditClose = useCallback(() => {
    setEditingAppeal(null);
  }, []);

  // Write-off remaining modal state
  const [writeOffRemainingAppeal, setWriteOffRemainingAppeal] = useState<Appeal | null>(null);
  const [isWriteOffRemainingLoading, setIsWriteOffRemainingLoading] = useState(false);
  const { mutateAsync: createCutsAppliedWriteOff } = useCreateCutsAppliedWriteOff();

  const handleWriteOffRemainingClick = useCallback((appeal: Appeal) => {
    setWriteOffRemainingAppeal(appeal);
  }, []);

  const handleWriteOffRemainingClose = useCallback(() => {
    setWriteOffRemainingAppeal(null);
  }, []);

  const handleWriteOffRemainingSubmit = useCallback(
    async (feesCut: number, expensesCut: number, comment: string, appealId: string) => {
      if (!writeOffRemainingAppeal) return;
      setIsWriteOffRemainingLoading(true);
      try {
        await createCutsAppliedWriteOff({
          invoiceId: writeOffRemainingAppeal.invoiceId,
          appealId,
          feesCut,
          expensesCut,
          comment,
        });
        showToast.success('Write-off created successfully.');
        setWriteOffRemainingAppeal(null);
      } catch (error) {
        showToast.error(getApiErrorMessage(error, 'Failed to create write-off. Please try again.'));
      } finally {
        setIsWriteOffRemainingLoading(false);
      }
    },
    [writeOffRemainingAppeal, createCutsAppliedWriteOff]
  );

  // Get current page invoice IDs for select-all logic (handles both views)
  const currentPageInvoiceIds = useMemo(() => {
    const dataToUse: Appeal[] =
      !isEmpty(rowSelected) &&
      (groupBy === GroupByOption.CLIENT || groupBy === GroupByOption.RESPONSIBLE)
        ? ((appealsByGroupIdData?.data ?? []) as Appeal[])
        : (appealsData as Appeal[]);
    return dataToUse.map(item => item.invoiceId);
  }, [appealsData, appealsByGroupIdData, rowSelected, groupBy]);

  // 5. COLUMN DEFINITION
  // clerkUserMap is now obtained from useClerkUserMap hook above

  const columns: DataTableColumn<any>[] = useMemo(() => {
    return generateAppealsColumns({
      actionSelectedAppeal,
      actionMenuRef: actionMenuRef as React.RefObject<HTMLDivElement>,
      actionRef,
      groupBy,
      rowSelected,
      tableParams,
      isAppealSelected: !isEmpty(rowSelected),
      appealSelectedParams,
      setIsActivityOpen,
      setSelectedActivityAppeal,
      setActionRef: (ref: HTMLElement | null) => setActionRef(ref as HTMLButtonElement | null),
      setActionSelectedAppeal,
      onMutationStateChange: handleMutationStateChange,
      handleCopyAttorneyResponseLink,
      handleViewAttorneyResponse,
      handleRollback: handleRollbackClick,
      handleEdit: handleEditClick,
      handleWriteOffRemaining: handleWriteOffRemainingClick,
      mutatingRows,
      currentPageInvoiceIds,
      clerkUserMap,
    });
  }, [
    actionSelectedAppeal,
    actionRef,
    groupBy,
    rowSelected,
    tableParams,
    appealSelectedParams,
    handleMutationStateChange,
    handleCopyAttorneyResponseLink,
    handleViewAttorneyResponse,
    handleRollbackClick,
    handleEditClick,
    handleWriteOffRemainingClick,
    mutatingRows,
    currentPageInvoiceIds,
    isDark,
  ]);

  /**
   * @description Performs the actual search update in appealSelectedParams after debounce.
   * @param {string} value - The search string.
   */
  const performSearchLocal = useCallback(
    (value: string) => {
      clearSelections();
      setAppealSelectedParams(prev => ({
        ...prev,
        page: 1,
        filters: {
          ...prev.filters,
          search: value,
        },
      }));
    },
    [clearSelections]
  );

  const debouncedAppealSelectedSearch = useMemo(
    () => debounce(performSearchLocal, 500),
    [performSearchLocal]
  );

  // Cleanup debounced local search on unmount
  useEffect(() => {
    return () => {
      debouncedAppealSelectedSearch.cancel();
    };
  }, [debouncedAppealSelectedSearch]);

  /**
   * @description Handler for local search input that sets local state and triggers debounce.
   * @param {string} value - The value from the search input.
   */
  const handleAppealSelectedSearch = useCallback(
    (value: string) => {
      setAppealSelectedSearch(value);
      debouncedAppealSelectedSearch(value);
    },
    [debouncedAppealSelectedSearch]
  );

  const handleAppealSelectedStatus = useCallback(
    (value: MultiValue<SelectOption> | null) => {
      const selectedOptions = (value ?? []) as SelectOption[];

      setAppealSelectedStatus(selectedOptions);
      clearSelections(); // Clear multi-select when changing status filter in group detail
      const statusValues = selectedOptions.map(item => item.value as AppealStatus);

      // Sync 'Include Closed Invoices' checkbox with status selection
      const includesClosed = statusValues.includes(AppealStatus.CLOSED);
      if (includesClosed) {
        setIsCheckedClosedInvoicesSelected(includesClosed);
      }

      if (isEmpty(selectedOptions)) {
        const status = [...NON_CLOSED_STATUSES];
        if (isCheckedClosedInvoicesSelected) {
          status.push(AppealStatus.CLOSED);
        }
        return setAppealSelectedParams({
          ...appealSelectedParams,
          page: 1,
          filters: {
            ...appealSelectedParams.filters,
            // If statusValues is empty, set filter to undefined to remove it from params
            status,
          },
        });
      }

      setAppealSelectedParams({
        ...appealSelectedParams,
        page: 1,
        filters: {
          ...appealSelectedParams.filters,
          // If statusValues is empty, set filter to undefined to remove it from params
          status: isEmpty(statusValues) ? undefined : statusValues,
        },
      });
    },
    [appealSelectedParams, isCheckedClosedInvoicesSelected, clearSelections]
  );

  const handleCheckedClosedInvoicesSelected = useCallback(() => {
    const nextChecked = !isCheckedClosedInvoicesSelected;
    setIsCheckedClosedInvoicesSelected(nextChecked);
    clearSelections();

    // Get current filter status, default to non-closed statuses if empty/undefined
    const currentFilterStatus = get(appealSelectedParams, 'filters.status', NON_CLOSED_STATUSES);

    let nextStatus: AppealStatus[];
    if (nextChecked) {
      // Add CLOSED status if not already present, ensuring uniqueness
      nextStatus = [...new Set([...currentFilterStatus, AppealStatus.CLOSED])];
    } else {
      // Filter out the CLOSED status
      nextStatus = currentFilterStatus.filter(
        (status: AppealStatus) => status !== AppealStatus.CLOSED
      );
      // Ensure we don't send an empty array if all non-closed were removed somehow
      if (isEmpty(nextStatus)) {
        nextStatus = NON_CLOSED_STATUSES;
      }
    }

    setAppealSelectedParams({
      ...appealSelectedParams,
      page: 1,
      filters: {
        ...appealSelectedParams.filters,
        status: nextStatus,
      },
    });
  }, [
    isCheckedClosedInvoicesSelected,
    appealSelectedParams,
    setAppealSelectedParams,
    clearSelections,
  ]);

  // 5. RENDERING LOGIC
  const currentTableData =
    !isEmpty(rowSelected) &&
    (groupBy === GroupByOption.CLIENT || groupBy === GroupByOption.RESPONSIBLE)
      ? (appealsByGroupIdData?.data ?? [])
      : appealsData;
  const currentPagination =
    !isEmpty(rowSelected) &&
    (groupBy === GroupByOption.CLIENT || groupBy === GroupByOption.RESPONSIBLE)
      ? (appealsByGroupIdData?.pagination ?? { total: 0, page: 1, limit: 25 })
      : paginationData;

  // Render Client/Attorney Group Detail View
  if (
    (groupBy === GroupByOption.CLIENT || groupBy === GroupByOption.RESPONSIBLE) &&
    !isEmpty(rowSelected)
  ) {
    return (
      <>
        <Card>
          {/* Component for the header and filters when viewing a single client's invoices */}
          <AppealsHeader
            groupBy={groupBy}
            rowSelected={rowSelected}
            clientDeadlineTier={clientDeadlineTier}
            handleClientDeadlineTierChange={handleClientDeadlineTierChange}
            searchLocal={appealSelectedSearch}
            statusSelectedLocal={appealSelectedStatus}
            assignedToLocal={assignedToLocal}
            isCheckedClosedInvoicesSelected={isCheckedClosedInvoicesSelected}
            isLoadingLocal={{
              search: isFetchingAppealsByGroupId,
              status: isFetchingAppealsByGroupId,
            }}
            handleSearchLocal={handleAppealSelectedSearch}
            handleSelectedStatusLocal={handleAppealSelectedStatus as any}
            handleCheckedClosedInvoicesSelected={handleCheckedClosedInvoicesSelected}
            handleFilterByAssignedToLocal={handleFilterByAssignedToLocal}
            refetch={refetch}
            onBackToClients={resetGroupSelection}
            selectedKeys={selectedKeys}
            onAssigneeChange={handleAssigneeChange}
            isBulkAssigning={isBulkAssigning}
            clerkUsersOptions={assigneeOptions}
          />

          {/* Table component for displaying individual invoices within the selected client group */}
          <AppealsTable
            key="local-table"
            rowKeyPrefix={`data-table-local-${groupBy}`}
            columns={columns}
            data={currentTableData as Appeal[]}
            pagination={currentPagination}
            isLoading={isFetchingAppealsByGroupId}
            error={error}
            onRetry={refetchAppealsByGroupId}
            tableParams={appealSelectedParams}
            setParams={pagination => {
              setAppealSelectedParams(prev => ({
                ...prev,
                ...pagination,
              }));
            }}
            onRowClick={handleOpenAppealDetail}
            getRowClassName={getRowClassName}
          />
        </Card>

        <InvoiceDetailDrawer clerkUserMap={clerkUserMap} />
        <AppealActivity
          isOpen={isActivityOpen}
          onClose={() => setIsActivityOpen(false)}
          invoiceNumber={selectedActivityAppeal?.invoiceNumber ?? ''}
          invoiceId={selectedActivityAppeal?.invoiceId ?? ''}
        />
      </>
    );
  }

  // Render Main Appeals List View (Grouped or Ungrouped)
  return (
    <>
      <Card>
        {/* Component for the main list header and filters */}
        <AppealsHeader
          groupBy={groupBy}
          rowSelected={rowSelected}
          clientDeadlineTier={clientDeadlineTier}
          handleClientDeadlineTierChange={handleClientDeadlineTierChange}
          search={search}
          statusSelected={statusSelected}
          assignedTo={assignedTo}
          isCheckedClosedInvoices={isCheckedClosedInvoices}
          isLoading={isFetching || isLoadingClerks}
          refetch={refetch}
          handleSearch={handleSearch}
          handleSelectedStatus={handleSelectedStatus as any}
          handleSelectedGroupBy={handleSelectedGroupBy}
          handleCheckedClosedInvoices={handleCheckedClosedInvoices}
          handleFiltersByAssignedTo={handleFiltersByAssignedTo}
          selectedKeys={selectedKeys}
          onAssigneeChange={handleAssigneeChange}
          isBulkAssigning={isBulkAssigning}
          clerkUsersOptions={assigneeOptions}
        />

        {/* Table component for displaying the main list of appeals or client groups */}
        <AppealsTable
          key={`table-server-${groupBy}`}
          rowKeyPrefix={`data-table-server-${groupBy}`}
          columns={columns}
          data={currentTableData as Appeal[] | AppealGroup[]}
          pagination={currentPagination}
          isLoading={isFetching}
          error={error}
          onRetry={refetch}
          tableParams={tableParams}
          setParams={setParams}
          onRowClick={handleRowClicked}
          getRowClassName={getRowClassName}
        />
        <AppealActivity
          isOpen={isActivityOpen}
          onClose={() => setIsActivityOpen(false)}
          invoiceNumber={selectedActivityAppeal?.invoiceNumber ?? ''}
          invoiceId={selectedActivityAppeal?.invoiceId ?? ''}
        />
      </Card>
      <InvoiceDetailDrawer clerkUserMap={clerkUserMap} />

      {/* Rollback Invoice Modal */}
      <RollbackInvoiceModal
        isOpen={isRollbackDialogOpen}
        isLoading={isRollbackLoading}
        invoiceNumber={appealToRollback?.invoiceNumber}
        invoiceId={appealToRollback?.invoiceId}
        onClose={handleRollbackCancel}
        onSubmit={handleRollbackConfirm}
      />

      {/* Edit Appeal Modal */}
      <EditAppealModal
        appeal={editingAppeal}
        params={tableParams}
        isAppealSelected={!isEmpty(rowSelected)}
        onClose={handleEditClose}
      />

      {/* Write-Off Remaining Modal */}
      <MarkWriteOffRemainingModal
        isOpen={!!writeOffRemainingAppeal}
        isLoading={isWriteOffRemainingLoading}
        appeal={writeOffRemainingAppeal}
        onClose={handleWriteOffRemainingClose}
        onSubmit={handleWriteOffRemainingSubmit}
      />
    </>
  );
}
